/**
 * Shared types for Employment Groups (SmartGroups)
 *
 * Employment Groups are dynamic groups of team members based on rules.
 * Behind the scenes:
 * - Each group has a 1:1 Tag in a "Team Member Groups" TagGroup
 * - Employments are assigned via ResourceTag(tagId, EMPLOYEE, employmentId)
 * - Membership is materialized by calling applyRules endpoint
 */
import { z } from "zod";
import { nameSchema, uuidSchema, hexColorOptionalSchema, } from "./validation-common.js";
// ===== RULE FIELD OPERATORS =====
/**
 * Field rule operators for IS/IS_NOT matching
 */
export const FieldOperatorSchema = z.enum(["IS", "IS_NOT"]);
/**
 * Skill rule operators for HAS/DOES_NOT_HAVE matching
 */
export const SkillOperatorSchema = z.enum(["HAS", "DOES_NOT_HAVE"]);
/**
 * Date rule operators for temporal matching
 */
export const DateOperatorSchema = z.enum(["BEFORE", "AFTER", "BETWEEN"]);
// ===== EMPLOYMENT TYPE ENUM (mirrors Prisma) =====
export const EmploymentTypeSchema = z.enum([
    "FULL_TIME",
    "PART_TIME",
    "CONTRACT",
    "TEMPORARY",
    "INTERN",
]);
// ===== RULE TYPE SCHEMAS =====
/**
 * Base rule fields shared by all rule types
 */
const BaseRuleSchema = z.object({
    id: uuidSchema.optional(), // Optional client-side ID for tracking in UI
});
/**
 * Job Role Family rule - match by position family (e.g., "Front of House")
 * Maps to "Job" in UI
 */
export const JobRoleFamilyRuleSchema = BaseRuleSchema.extend({
    type: z.literal("JOB_ROLE_FAMILY"),
    operator: FieldOperatorSchema,
    values: z.array(uuidSchema).min(1, "At least one job is required"),
});
/**
 * Job Role rule - match by specific position (e.g., "Barista")
 * Maps to "Position" in UI
 */
export const JobRoleRuleSchema = BaseRuleSchema.extend({
    type: z.literal("JOB_ROLE"),
    operator: FieldOperatorSchema,
    values: z.array(uuidSchema).min(1, "At least one position is required"),
});
/**
 * Location rule - match by work location
 */
export const LocationRuleSchema = BaseRuleSchema.extend({
    type: z.literal("LOCATION"),
    operator: FieldOperatorSchema,
    values: z.array(uuidSchema).min(1, "At least one location is required"),
});
/**
 * District rule - match employments at locations within specified districts
 */
export const DistrictRuleSchema = BaseRuleSchema.extend({
    type: z.literal("DISTRICT"),
    operator: FieldOperatorSchema,
    values: z.array(uuidSchema).min(1, "At least one district is required"),
});
/**
 * Employment Type rule - match by employment classification
 * Maps to "Type of team member" in UI (Part-time, Full-time, etc.)
 */
export const EmploymentTypeRuleSchema = BaseRuleSchema.extend({
    type: z.literal("EMPLOYMENT_TYPE"),
    operator: FieldOperatorSchema,
    values: z
        .array(EmploymentTypeSchema)
        .min(1, "At least one employment type is required"),
});
/**
 * Skill rule - match by employee skills
 */
export const SkillRuleSchema = BaseRuleSchema.extend({
    type: z.literal("SKILL"),
    operator: SkillOperatorSchema,
    values: z.array(uuidSchema).min(1, "At least one skill is required"),
});
/**
 * Employment start date rule - match by hire date
 * Maps to "Hire Date" in UI
 */
export const EmploymentStartDateRuleSchema = BaseRuleSchema.extend({
    type: z.literal("EMPLOYMENT_START_DATE"),
    operator: DateOperatorSchema,
    // For BEFORE/AFTER: use date1 only
    // For BETWEEN: use date1 (start) and date2 (end), both inclusive
    date1: z.iso.datetime({ message: "Invalid date format for date1" }),
    date2: z.iso.datetime().optional(), // Required for BETWEEN
}).refine((data) => {
    if (data.operator === "BETWEEN" && !data.date2) {
        return false;
    }
    return true;
}, { message: "date2 is required for BETWEEN operator", path: ["date2"] });
// ============================================================================
// Response Schemas with Resolved Values
// ============================================================================
/**
 * Resolved value - contains both ID and human-readable name
 * Used in response schemas to provide displayable information to the frontend
 */
export const ResolvedValueSchema = z.object({
    id: z.string(),
    name: z.string(),
});
/**
 * Job Role Family rule response - with resolved job family names
 */
export const JobRoleFamilyRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("JOB_ROLE_FAMILY"),
    operator: FieldOperatorSchema,
    values: z.array(ResolvedValueSchema).min(1, "At least one job is required"),
});
/**
 * Job Role rule response - with resolved position names
 */
export const JobRoleRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("JOB_ROLE"),
    operator: FieldOperatorSchema,
    values: z
        .array(ResolvedValueSchema)
        .min(1, "At least one position is required"),
});
/**
 * Location rule response - with resolved location names
 */
export const LocationRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("LOCATION"),
    operator: FieldOperatorSchema,
    values: z
        .array(ResolvedValueSchema)
        .min(1, "At least one location is required"),
});
/**
 * District rule response - with resolved district names
 */
export const DistrictRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("DISTRICT"),
    operator: FieldOperatorSchema,
    values: z
        .array(ResolvedValueSchema)
        .min(1, "At least one district is required"),
});
/**
 * Employment Type rule response - with resolved { id, name } values for consistency
 */
export const EmploymentTypeRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("EMPLOYMENT_TYPE"),
    operator: FieldOperatorSchema,
    values: z
        .array(ResolvedValueSchema)
        .min(1, "At least one employment type is required"),
});
/**
 * Skill rule response - with resolved skill names
 */
export const SkillRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("SKILL"),
    operator: SkillOperatorSchema,
    values: z.array(ResolvedValueSchema).min(1, "At least one skill is required"),
});
/**
 * Employment start date rule response - no values to resolve, same as input schema
 */
export const EmploymentStartDateRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("EMPLOYMENT_START_DATE"),
    operator: DateOperatorSchema,
    date1: z.iso.datetime({ message: "Invalid date format for date1" }),
    date2: z.iso.datetime().optional(),
}).refine((data) => {
    if (data.operator === "BETWEEN" && !data.date2) {
        return false;
    }
    return true;
}, { message: "date2 is required for BETWEEN operator", path: ["date2"] });
/**
 * Discriminated union of all rule response types with resolved values
 */
export const SmartGroupRuleResponseSchema = z.discriminatedUnion("type", [
    JobRoleFamilyRuleResponseSchema,
    JobRoleRuleResponseSchema,
    LocationRuleResponseSchema,
    DistrictRuleResponseSchema,
    EmploymentTypeRuleResponseSchema,
    SkillRuleResponseSchema,
    EmploymentStartDateRuleResponseSchema,
]);
/**
 * Array of smart group rules with resolved values
 */
export const SmartGroupRulesResponseSchema = z
    .array(SmartGroupRuleResponseSchema)
    .default([]);
/**
 * Discriminated union of all rule types
 */
export const SmartGroupRuleSchema = z.discriminatedUnion("type", [
    JobRoleFamilyRuleSchema,
    JobRoleRuleSchema,
    LocationRuleSchema,
    DistrictRuleSchema,
    EmploymentTypeRuleSchema,
    SkillRuleSchema,
    EmploymentStartDateRuleSchema,
]);
/**
 * Array of rules with validation
 */
export const SmartGroupRulesSchema = z.array(SmartGroupRuleSchema);
// ===== LOGIC SCHEMAS =====
/**
 * Group type: SMART (rule-based) or MANUAL (explicit member assignment)
 */
export const GroupTypeSchema = z.enum(["SMART", "MANUAL"]);
/**
 * How rules combine (all rules must match vs any rule must match)
 * Default: AND (all rules must match)
 */
export const RuleLogicSchema = z.enum(["AND", "OR"]);
/**
 * How values within a rule combine
 * OR: any selected value matches (e.g., Location IS Boston OR Chicago)
 * AND: all selected values must match (e.g., has Skill A AND Skill B)
 * Default: OR
 */
export const ValueLogicSchema = z.enum(["AND", "OR"]);
// ===== INPUT SCHEMAS =====
/**
 * Schema for creating a SMART employment group (rule-based)
 */
export const CreateSmartGroupInputSchema = z.object({
    groupType: z.literal("SMART"),
    name: nameSchema,
    color: hexColorOptionalSchema,
    rules: SmartGroupRulesSchema.min(1, "At least one rule is required"),
    ruleLogic: RuleLogicSchema.default("AND"),
    valueLogic: ValueLogicSchema.default("OR"),
    channelEnabled: z.boolean().default(true).optional(),
});
/**
 * Schema for creating a MANUAL employment group (explicit member assignment)
 */
export const CreateManualGroupInputSchema = z.object({
    groupType: z.literal("MANUAL"),
    name: nameSchema,
    color: hexColorOptionalSchema,
    channelEnabled: z.boolean().default(true).optional(),
});
/**
 * Schema for creating an employment group (discriminated union)
 */
export const CreateEmploymentGroupInputSchema = z.discriminatedUnion("groupType", [
    CreateSmartGroupInputSchema,
    CreateManualGroupInputSchema,
]);
/**
 * Schema for updating a SMART employment group
 */
export const UpdateSmartGroupInputSchema = z.object({
    name: nameSchema.optional(),
    color: hexColorOptionalSchema,
    rules: SmartGroupRulesSchema.min(1, "At least one rule is required").optional(),
    ruleLogic: RuleLogicSchema.optional(),
    valueLogic: ValueLogicSchema.optional(),
});
/**
 * Schema for updating a MANUAL employment group
 */
export const UpdateManualGroupInputSchema = z.object({
    name: nameSchema.optional(),
    color: hexColorOptionalSchema,
});
/**
 * Schema for updating an employment group
 * Note: groupType cannot be changed after creation
 */
export const UpdateEmploymentGroupInputSchema = z.object({
    name: nameSchema.optional(),
    color: hexColorOptionalSchema,
    rules: SmartGroupRulesSchema.min(1, "At least one rule is required").optional(),
    ruleLogic: RuleLogicSchema.optional(),
    valueLogic: ValueLogicSchema.optional(),
    channelEnabled: z.boolean().optional(),
});
// ===== MEMBER MANAGEMENT SCHEMAS (Manual Groups) =====
/**
 * Schema for adding members to a manual group
 */
export const AddGroupMembersInputSchema = z.object({
    employmentIds: z.array(uuidSchema).min(1, "At least one employment ID is required"),
});
/**
 * Schema for removing a member from a manual group
 */
export const RemoveGroupMemberInputSchema = z.object({
    employmentId: uuidSchema,
});
// ===== RESPONSE SCHEMAS =====
/**
 * Employment group member response schema
 */
export const EmploymentGroupMemberResponseSchema = z.object({
    employmentId: z.string().uuid(),
    userId: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    positions: z.array(z.string()),
    locations: z.array(z.string()),
});
/**
 * Compact employment group response (for lists)
 */
export const EmploymentGroupResponseSchema = z.object({
    id: z.uuid(),
    groupType: GroupTypeSchema,
    name: z.string(),
    color: z.string().nullable(),
    companyId: z.uuid(),
    tagId: z.uuid(),
    rules: SmartGroupRulesResponseSchema,
    ruleLogic: RuleLogicSchema,
    valueLogic: ValueLogicSchema,
    memberCount: z.number().int().nonnegative(),
    lastAppliedAt: z.iso.datetime().nullable(),
    channelEnabled: z.boolean(),
    conversationId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Full employment group response with members
 */
export const EmploymentGroupDetailResponseSchema = EmploymentGroupResponseSchema.extend({
    members: z.array(EmploymentGroupMemberResponseSchema),
});
/**
 * Apply rules result response
 */
export const ApplyRulesResultSchema = z.object({
    group: EmploymentGroupDetailResponseSchema,
    added: z.number().int().nonnegative(),
    removed: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
});
// ===== PARAMETER SCHEMAS =====
export const EmploymentGroupIdParamSchema = z.object({
    groupId: uuidSchema,
});
/**
 * List response schema for employment groups
 */
export const ListEmploymentGroupsResponseSchema = z.object({
    groups: z.array(EmploymentGroupResponseSchema),
    total: z.number().int().nonnegative(),
});
/**
 * Apply rules response schema - returns member count changes
 */
export const ApplyRulesResponseSchema = z.object({
    memberCount: z.number().int().nonnegative(),
    addedCount: z.number().int().nonnegative(),
    removedCount: z.number().int().nonnegative(),
});
/**
 * Add members response schema - for manual groups
 */
export const AddMembersResponseSchema = z.object({
    addedCount: z.number().int().nonnegative(),
    memberCount: z.number().int().nonnegative(),
});
/**
 * Remove member response schema - for manual groups
 */
export const RemoveMemberResponseSchema = z.object({
    removed: z.boolean(),
    memberCount: z.number().int().nonnegative(),
});
// ===== AVAILABLE RULE FIELDS =====
/**
 * Single option for a rule field dropdown
 */
export const FieldOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
});
/**
 * Available fields and values for rule configuration UI
 */
export const AvailableRuleFieldsResponseSchema = z.object({
    jobRoleFamilies: z.array(FieldOptionSchema),
    jobRoles: z.array(FieldOptionSchema),
    locations: z.array(FieldOptionSchema),
    districts: z.array(FieldOptionSchema),
    skills: z.array(FieldOptionSchema),
    employmentTypes: z.array(FieldOptionSchema),
});
